import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Seo from "@/components/site/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function centsToDollars(cents) {
  if (cents == null || isNaN(cents)) return "0.00";
  const abs = Math.abs(parseInt(cents, 10));
  return `${Math.floor(abs / 100)}.${String(abs % 100).padStart(2, "0")}`;
}

function formatDenverDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  if (!y || !m || !d) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(dt);
}

export default function CheckoutSuccess() {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    setSessionId(sid || "");
    if (!sid) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/public/order-status`, {
          params: { session_id: sid },
        });
        if (data?.found && data?.fulfillment_status === "fulfilled") {
          setOrder(data);
          setLoading(false);
          return;
        }
        if (data?.found && data?.fulfillment_status === "failed_oversold") {
          setOrder(data);
          setLoading(false);
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts < 12) setTimeout(poll, 2000);
      else {
        setLoading(false);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Seo
        title="Tickets confirmed — The Next Stage"
        description="Your tickets and check-in QR codes."
        path="/checkout/success"
      />
      <section
        data-testid="checkout-success-page"
        className="max-w-3xl mx-auto px-6 py-20 text-white"
      >
        <div className="text-xs tracking-[0.25em] opacity-60 mb-3 text-center">
          THE NEXT STAGE
        </div>

        {loading && (
          <div data-testid="checkout-success-confirming" className="text-center">
            <h1 className="text-3xl sm:text-5xl font-semibold mb-6">
              Confirming your tickets…
            </h1>
            <p className="text-lg opacity-80 leading-relaxed">
              Stripe has received your payment. We're attaching your ticket QR
              codes — this usually takes a few seconds.
            </p>
          </div>
        )}

        {!loading && !order && (
          <div data-testid="checkout-success-unknown" className="text-center">
            <h1 className="text-3xl sm:text-5xl font-semibold mb-6">
              Order not found
            </h1>
            <p className="text-lg opacity-80 leading-relaxed mb-8">
              If you've just completed a payment, please give it another minute
              and refresh. If this persists, contact us with your receipt.
            </p>
            <Link
              to="/tickets"
              className="inline-block border border-white/30 px-6 py-3 hover:border-white/60 transition-colors"
            >
              Back to tickets
            </Link>
          </div>
        )}

        {!loading && order?.fulfillment_status === "failed_oversold" && (
          <div data-testid="checkout-success-oversold" className="text-center">
            <h1 className="text-3xl sm:text-5xl font-semibold mb-6">
              Payment received — small hiccup
            </h1>
            <p className="text-lg opacity-80 leading-relaxed mb-6">
              In the few seconds it took to process your payment, the show
              filled up. We've flagged your order for a full refund and will
              email you shortly. <strong>No tickets were issued.</strong>
            </p>
            <Link
              to="/"
              className="inline-block border border-white/30 px-6 py-3 hover:border-white/60 transition-colors"
            >
              Back to home
            </Link>
          </div>
        )}

        {!loading && order?.fulfillment_status === "fulfilled" && (
          <FulfilledView order={order} />
        )}
      </section>
    </>
  );
}

function FulfilledView({ order }) {
  return (
    <div data-testid="checkout-success-fulfilled">
      <h1 className="text-3xl sm:text-5xl font-semibold mb-4 text-center">
        You're in.
      </h1>
      <p className="text-center text-lg opacity-80 leading-relaxed mb-10">
        Your tickets are confirmed. Save or screenshot the QR codes below —
        each one is a check-in code. We've also emailed a copy to you.
      </p>

      <table className="w-full text-sm border border-white/10 mb-10">
        <tbody>
          <Row label="Event" value={order.event?.name} testid="success-event-name" />
          <Row
            label="Date"
            value={`${formatDenverDate(order.event?.event_date)} (Mountain Time)`}
            testid="success-event-date"
          />
          <Row label="Venue" value={order.event?.venue} testid="success-event-venue" />
          <Row
            label="Tier"
            value={`${order.tier_name} × ${order.quantity}`}
            testid="success-tier-line"
          />
          <Row
            label="Total paid"
            value={`$${centsToDollars(order.total_cents)}`}
            testid="success-total"
          />
        </tbody>
      </table>

      <ul
        data-testid="success-tickets-list"
        className="grid sm:grid-cols-2 gap-6"
      >
        {(order.tickets || []).map((t, i) => (
          <li
            key={t.token_preview + i}
            data-testid={`success-ticket-${i + 1}`}
            className="border border-white/15 p-6 flex flex-col items-center bg-black/40"
          >
            <div className="text-[11px] tracking-[0.25em] uppercase text-amber-300 mb-3">
              Ticket {i + 1} of {order.quantity}
            </div>
            <img
              data-testid={`success-ticket-qr-${i + 1}`}
              src={t.qr_data_url}
              alt={`Ticket ${i + 1} QR code`}
              className="bg-white p-2"
              style={{ width: 220, height: 220 }}
            />
            <div
              className="text-[11px] opacity-50 mt-3 font-mono"
              data-testid={`success-ticket-token-${i + 1}`}
            >
              {t.token_preview}…
            </div>
          </li>
        ))}
      </ul>

      <p className="text-center text-xs opacity-60 mt-10 leading-relaxed">
        Show the QR at the door. One scan, one entry per ticket.
        <br />
        Lost the email? It's safe to come back to this page anytime with your
        original confirmation link.
      </p>

      <div className="text-center mt-10">
        <Link
          to="/"
          className="inline-block border border-white/30 px-6 py-3 hover:border-white/60 transition-colors"
        >
          Back to home
        </Link>
      </div>
    </div>
  );
}

function Row({ label, value, testid }) {
  return (
    <tr className="border-t border-white/10 first:border-t-0">
      <td className="px-4 py-3 text-xs uppercase tracking-wider opacity-60 w-1/3">
        {label}
      </td>
      <td className="px-4 py-3" data-testid={testid}>
        {value || "—"}
      </td>
    </tr>
  );
}
